import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
	FileText,
	Settings,
	Send,
	CheckCircle,
	AlertCircle,
	XCircle,
	AlertTriangle,
	ThumbsUp,
	Flag,
	Play,
} from 'lucide-react'
import { Button, ConfirmDialog } from '@components/common'
import { TaskNotesSection, TaskDocumentsSection, TaskConfigurationModal } from '@components/features/tasks'
import authService from '@services/authService'
import taskService from '@services/taskService'
import './TaskDetailsTab.scss'

/**
 * TaskDetailsTab Component
 *
 * Displays task details with workflow-appropriate action buttons:
 *
 * Workflow Flow:
 * 1. First step (assignee): in_progress → "Submit for Review" → moves to step 2
 * 2. Middle steps (approvers): in_progress → "Accept" / "Reject"
 *    - If Accept → pending_review → "Submit for Review" → moves to next step
 *    - If Reject → rejected → goes back to previous step
 * 3. Last step (approver): in_progress → "Accept" / "Reject"
 *    - If Accept → pending_review → "Confirm as Completed" → workflow complete (approved)
 */
const TaskDetailsTab = ({
	task,
	taskId,
	workflow,
	onTaskUpdate,
	onRefresh,
	onTaskAction,
	actionLoading = false,
}) => {
	// Get user directly from authService (more reliable than context)
	const user = authService.getCurrentUser()
	const [configModalOpen, setConfigModalOpen] = useState(false)
	const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false)
	const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false)
	const [rejectModalOpen, setRejectModalOpen] = useState(false)
	const [rejectRemarks, setRejectRemarks] = useState('')
	const [documentsCount, setDocumentsCount] = useState(task?.documents_count || 0)

	// Handle documents count change from TaskDocumentsSection
	const handleDocumentsChange = (count) => {
		setDocumentsCount(count)
		// Also update parent task state
		onTaskUpdate?.({ documents_count: count })
	}

	// Get user role slug
	const userRoleSlug = useMemo(() => {
		if (!user) return null
		return user.role_slug || user.role?.slug
	}, [user])

	// Check if user can configure tasks (CM/PM/Admin)
	const canConfigureTasks = useMemo(() => {
		if (!userRoleSlug) return false
		return ['super-admin', 'company-admin', 'compliance-manager', 'project-manager'].includes(userRoleSlug)
	}, [userRoleSlug])

	// Check if task is locked
	const isLocked = useMemo(() => {
		if (!task) return true
		return task.is_locked === 1 || task.workflow_status === 'approved'
	}, [task])

	// Check if this is the first workflow step
	const isFirstStep = useMemo(() => {
		return taskService.isFirstWorkflowStep(task)
	}, [task])

	// Check if this is the last workflow step
	const isLastStep = useMemo(() => {
		return taskService.isLastWorkflowStep(task)
	}, [task])

	// Check if user is assigned to this task
	const isAssignedToUser = useMemo(() => {
		if (!user || !task) return false
		return Number(task.assigned_to) === Number(user.id)
	}, [task, user])

	// Check if user's role matches current workflow step
	const isUserRoleForCurrentStep = useMemo(() => {
		if (!task || !task.workflow_info || !task.workflow_info.current_step) return false
		return task.workflow_info.current_step.role_slug === userRoleSlug
	}, [task, userRoleSlug])

	// ============================================
	// ACTION VISIBILITY CONDITIONS
	// ============================================
	// Workflow Flow:
	// 1. not_started/rejected → Start Task → in_progress
	// 2. in_progress → Submit for Review → pending_review
	// 3. pending_review → Accept → in_progress (approver working)
	// 4. in_progress (not last step) → Submit for Review → pending_review
	// 5. in_progress (last step) → Confirm as Completed → approved (100%)

	// User can start task when status is not_started, pending_assignment, or rejected
	const canStartTask = useMemo(() => {
		if (!task || !isAssignedToUser) return false
		const validStartStatuses = ['not_started', 'pending_assignment', 'rejected', '']
		return validStartStatuses.includes(task.workflow_status)
	}, [task, isAssignedToUser])

	// User can submit when task is in_progress and they are assigned
	const canSubmitForReview = useMemo(() => {
		if (!task || !isAssignedToUser) return false
		return task.workflow_status === 'in_progress'
	}, [task, isAssignedToUser])

	// Approver: Can accept when task is pending_review and user's role matches current step
	const canAcceptTask = useMemo(() => {
		if (!task) return false
		if (task.workflow_status !== 'pending_review') return false
		return isUserRoleForCurrentStep
	}, [task, isUserRoleForCurrentStep])

	// Approver: Can reject when task is pending_review
	const canRejectTask = useMemo(() => {
		if (!task) return false
		if (task.workflow_status !== 'pending_review') return false
		return isUserRoleForCurrentStep
	}, [task, isUserRoleForCurrentStep])

	// Last step user: Can complete when in_progress at last step
	const canCompleteTask = useMemo(() => {
		if (!task || !isAssignedToUser) return false
		if (task.workflow_status !== 'in_progress') return false
		return isLastStep
	}, [task, isAssignedToUser, isLastStep])

	// Action handlers - Start Task (for rejected tasks to resume work)
	const handleStartTask = () => {
		onTaskAction?.('start', {})
	}

	// Legacy compatibility aliases
	const canResumeAfterRejection = canStartTask && task?.workflow_status === 'rejected'
	const canStartNewTask = canStartTask && ['not_started', 'pending_assignment', ''].includes(task?.workflow_status)
	const canSubmitFirstStep = canSubmitForReview && isFirstStep
	const canAcceptOrReject = canAcceptTask
	const canSubmitAfterAccept = canSubmitForReview && !isFirstStep && !isLastStep
	const canComplete = canCompleteTask

	// Check readiness for submission
	const getSubmitReadiness = useMemo(() => {
		if (!task) return { ready: false, issues: [] }

		const issues = []
		const hasDocuments = documentsCount > 0
		// Evidence is ALWAYS required at the first workflow step
		// This ensures the initial assignee uploads evidence before submitting
		const evidenceRequired = isFirstStep || task.evidence_required === 1

		if (evidenceRequired && !hasDocuments) {
			issues.push('Evidence documents are required but none have been uploaded')
		}

		return {
			ready: issues.length === 0,
			issues,
			hasDocuments,
			evidenceRequired,
		}
	}, [task, isFirstStep, documentsCount])

	// ============================================
	// HANDLERS
	// ============================================

	const handleConfigSave = (updatedTask) => {
		onTaskUpdate?.(updatedTask)
	}

	// Handle submit for review - open confirmation dialog
	const handleSubmitForReview = () => {
		if (!getSubmitReadiness.ready) return
		setSubmitConfirmOpen(true)
	}

	// Handle confirmed submission
	const handleSubmitConfirm = () => {
		setSubmitConfirmOpen(false)
		onTaskAction?.('submit', {
			notes: '',
			evidence_attached: documentsCount > 0,
		})
	}

	// Handle accept task
	const handleAccept = () => {
		onTaskAction?.('accept', {})
	}

	// Handle complete task (last step)
	const handleCompleteClick = () => {
		setCompleteConfirmOpen(true)
	}

	const handleCompleteConfirm = () => {
		setCompleteConfirmOpen(false)
		onTaskAction?.('complete', { notes: '' })
	}

	// Handle reject - open modal for rejection remarks
	const handleRejectClick = () => {
		setRejectRemarks('')
		setRejectModalOpen(true)
	}

	const handleRejectConfirm = () => {
		if (!rejectRemarks.trim()) return
		setRejectModalOpen(false)
		onTaskAction?.('reject', { remarks: rejectRemarks })
		setRejectRemarks('')
	}

	const handleRejectCancel = () => {
		setRejectModalOpen(false)
		setRejectRemarks('')
	}

	// Format date for display
	const formatDate = (dateString) => {
		if (!dateString) return ''
		const date = new Date(dateString)
		return date.toLocaleString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		})
	}

	if (!task) {
		return (
			<div className="task-details-tab task-details-tab--empty">
				<FileText size={48} />
				<p>No task data available</p>
			</div>
		)
	}

	// Determine if there's any action section to show
	const hasActionSection = canStartNewTask || canResumeAfterRejection || canSubmitFirstStep ||
		canAcceptOrReject || canSubmitAfterAccept || canComplete

	return (
		<div className="task-details-tab">
			<div className="task-details-tab__content">
				{/* ============================================ */}
				{/* ACTION SECTIONS - ALWAYS AT TOP FOR VISIBILITY */}
				{/* ============================================ */}

				{/* Rejection Alert - Show when task is rejected */}
				{task.workflow_status === 'rejected' && task.last_rejection_reason && (
					<div className="task-details-tab__rejection-alert">
						<div className="rejection-alert__header">
							<AlertTriangle size={20} />
							<h3>Task Rejected</h3>
						</div>
						<div className="rejection-alert__content">
							<p className="rejection-alert__reason">{task.last_rejection_reason}</p>
							<div className="rejection-alert__meta">
								{task.rejected_by_name && (
									<span className="rejection-alert__by">
										Rejected by: <strong>{task.rejected_by_name}</strong>
									</span>
								)}
								{task.last_rejected_at && (
									<span className="rejection-alert__date">
										on {formatDate(task.last_rejected_at)}
									</span>
								)}
							</div>
						</div>
						<p className="rejection-alert__instruction">
							Please address the feedback above and re-submit the task for review.
						</p>
					</div>
				)}

				{/* NEW TASK: Start Task Section */}
				{canStartNewTask && (
					<div className="task-details-tab__action-card task-details-tab__start-section">
						<div className="action-card__icon">
							<Play size={28} />
						</div>
						<div className="action-card__content">
							<h3 className="action-card__title">Start Task</h3>
							<p className="action-card__description">
								This task is assigned to you. Click to begin working on it.
							</p>
						</div>
						<div className="action-card__actions">
							<Button
								variant="primary"
								size="large"
								onClick={handleStartTask}
								disabled={actionLoading}
							>
								<Play size={18} />
								{actionLoading ? 'Starting...' : 'Start Task'}
							</Button>
						</div>
					</div>
				)}

				{/* REJECTED TASK: Resume Work After Rejection */}
				{canResumeAfterRejection && (
					<div className="task-details-tab__action-card task-details-tab__start-section">
						<div className="action-card__icon">
							<Play size={28} />
						</div>
						<div className="action-card__content">
							<h3 className="action-card__title">Resume Work</h3>
							<p className="action-card__description">
								Review the feedback above, make changes, and resume working on this task.
							</p>
						</div>
						<div className="action-card__actions">
							<Button
								variant="primary"
								size="large"
								onClick={handleStartTask}
								disabled={actionLoading}
							>
								<Play size={18} />
								{actionLoading ? 'Starting...' : 'Resume Task'}
							</Button>
						</div>
					</div>
				)}

				{/* FIRST STEP: Submit for Review Section */}
				{canSubmitFirstStep && (
					<div className="task-details-tab__action-card task-details-tab__submit-section">
						<div className="action-card__icon action-card__icon--success">
							<Send size={28} />
						</div>
						<div className="action-card__content">
							<h3 className="action-card__title">Ready to Submit?</h3>
							<div className="action-card__checklist">
								<div className={`checklist-item ${task.description ? 'checklist-item--done' : ''}`}>
									<span className="checklist-icon">
										{task.description ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
									</span>
									<span className="checklist-text">Task description reviewed</span>
								</div>
								<div className={`checklist-item ${getSubmitReadiness.hasDocuments ? 'checklist-item--done' : 'checklist-item--warning'}`}>
									<span className="checklist-icon">
										{getSubmitReadiness.hasDocuments ? (
											<CheckCircle size={16} />
										) : (
											<AlertCircle size={16} />
										)}
									</span>
									<span className="checklist-text">
										{getSubmitReadiness.hasDocuments
											? `Evidence uploaded (${task.documents_count || 0} files)`
											: 'Evidence document required'}
									</span>
								</div>
							</div>
							{!getSubmitReadiness.ready && getSubmitReadiness.issues.length > 0 && (
								<div className="action-card__warning">
									<AlertCircle size={14} />
									<span>{getSubmitReadiness.issues[0]}</span>
								</div>
							)}
						</div>
						<div className="action-card__actions">
							<Button
								variant="success"
								size="large"
								onClick={handleSubmitForReview}
								disabled={actionLoading || !getSubmitReadiness.ready}
							>
								<Send size={18} />
								{actionLoading ? 'Submitting...' : 'Submit for Review'}
							</Button>
						</div>
					</div>
				)}

				{/* APPROVER: Accept/Reject Section */}
				{canAcceptOrReject && (
					<div className="task-details-tab__action-card task-details-tab__approval-section">
						<div className="action-card__icon action-card__icon--primary">
							<ThumbsUp size={28} />
						</div>
						<div className="action-card__content">
							<h3 className="action-card__title">Review Required</h3>
							<p className="action-card__description">
								Review the task details, notes, and evidence documents below. Accept to proceed or reject with feedback.
							</p>
						</div>
						<div className="action-card__actions action-card__actions--dual">
							<Button
								variant="success"
								size="large"
								onClick={handleAccept}
								disabled={actionLoading}
							>
								<ThumbsUp size={18} />
								{actionLoading ? 'Accepting...' : 'Accept'}
							</Button>
							<Button
								variant="danger"
								size="large"
								onClick={handleRejectClick}
								disabled={actionLoading}
							>
								<XCircle size={18} />
								Reject
							</Button>
						</div>
					</div>
				)}

				{/* AFTER ACCEPT (Non-Last Step): Submit to Next Step */}
				{canSubmitAfterAccept && (
					<div className="task-details-tab__action-card task-details-tab__submit-section">
						<div className="action-card__icon action-card__icon--success">
							<Send size={28} />
						</div>
						<div className="action-card__content">
							<h3 className="action-card__title">Submit to Next Step</h3>
							<p className="action-card__description">
								You have accepted this task. Submit it to the next workflow step for further review.
							</p>
						</div>
						<div className="action-card__actions action-card__actions--dual">
							<Button
								variant="success"
								size="large"
								onClick={handleSubmitForReview}
								disabled={actionLoading}
							>
								<Send size={18} />
								{actionLoading ? 'Submitting...' : 'Submit for Review'}
							</Button>
							{canRejectTask && (
								<Button
									variant="outline"
									size="large"
									onClick={handleRejectClick}
									disabled={actionLoading}
								>
									<XCircle size={18} />
									Reject
								</Button>
							)}
						</div>
					</div>
				)}

				{/* LAST STEP: Confirm as Completed */}
				{canComplete && (
					<div className="task-details-tab__action-card task-details-tab__complete-section">
						<div className="action-card__icon action-card__icon--warning">
							<Flag size={28} />
						</div>
						<div className="action-card__content">
							<h3 className="action-card__title">Final Step</h3>
							<p className="action-card__description">
								This is the final workflow step. Confirm completion to mark this task as 100% complete.
							</p>
						</div>
						<div className="action-card__actions action-card__actions--dual">
							<Button
								variant="success"
								size="large"
								onClick={handleCompleteClick}
								disabled={actionLoading}
							>
								<Flag size={18} />
								{actionLoading ? 'Completing...' : 'Complete Task'}
							</Button>
							{canRejectTask && (
								<Button
									variant="outline"
									size="large"
									onClick={handleRejectClick}
									disabled={actionLoading}
								>
									<XCircle size={18} />
									Reject
								</Button>
							)}
						</div>
					</div>
				)}

				{/* ============================================ */}
				{/* CONTENT SECTIONS - BELOW ACTIONS */}
				{/* ============================================ */}

				{/* Description Section */}
				<div className="task-details-tab__section task-details-tab__description-section">
					<div className="section-header">
						<h3>
							<FileText size={18} />
							Task Description
						</h3>
						{canConfigureTasks && !isLocked && (
							<Button
								variant="ghost"
								size="small"
								onClick={() => setConfigModalOpen(true)}
							>
								<Settings size={14} />
								Configure
							</Button>
						)}
					</div>
					<div className="task-details-tab__description-content">
						{task.description ? (
							<p className="task-details-tab__description">{task.description}</p>
						) : (
							<p className="task-details-tab__description task-details-tab__description--empty">
								No description provided for this task.
							</p>
						)}
					</div>
				</div>

				{/* Notes & Conversation Section */}
				<TaskNotesSection taskId={taskId} isLocked={isLocked} />

				{/* Evidence Documents Section */}
				<TaskDocumentsSection
					taskId={taskId}
					isLocked={isLocked}
					evidenceRequired={isFirstStep || task.evidence_required === 1}
					onDocumentsChange={handleDocumentsChange}
				/>
			</div>

			{/* Task Configuration Modal */}
			<TaskConfigurationModal
				isOpen={configModalOpen}
				onClose={() => setConfigModalOpen(false)}
				task={task}
				onSave={handleConfigSave}
			/>

			{/* Submit for Review Confirmation Dialog */}
			<ConfirmDialog
				isOpen={submitConfirmOpen}
				onConfirm={handleSubmitConfirm}
				onCancel={() => setSubmitConfirmOpen(false)}
				title="Submit for Review"
				message="Are you sure you want to submit this task for review? Once submitted, it will move to the next workflow step."
				confirmText="Submit"
				cancelText="Cancel"
				variant="primary"
				loading={actionLoading}
			/>

			{/* Complete Task Confirmation Dialog */}
			<ConfirmDialog
				isOpen={completeConfirmOpen}
				onConfirm={handleCompleteConfirm}
				onCancel={() => setCompleteConfirmOpen(false)}
				title="Confirm Task Completion"
				message="Are you sure you want to mark this task as completed? This will finalize the workflow and the task will be locked."
				confirmText="Complete Task"
				cancelText="Cancel"
				variant="success"
				loading={actionLoading}
			/>

			{/* Reject Task Modal */}
			{rejectModalOpen && (
				<div className="reject-modal-overlay" onClick={handleRejectCancel}>
					<div className="reject-modal" onClick={(e) => e.stopPropagation()}>
						<div className="reject-modal__header">
							<XCircle size={24} />
							<h3>Reject Task</h3>
						</div>
						<p className="reject-modal__message">
							Please provide remarks explaining why this task is being rejected.
							The task will be returned to the previous assignee for revision.
						</p>
						<textarea
							className="reject-modal__textarea"
							placeholder="Enter rejection remarks (required)..."
							value={rejectRemarks}
							onChange={(e) => setRejectRemarks(e.target.value)}
							rows={4}
							autoFocus
						/>
						<div className="reject-modal__actions">
							<Button
								variant="outline"
								onClick={handleRejectCancel}
								disabled={actionLoading}
							>
								Cancel
							</Button>
							<Button
								variant="danger"
								onClick={handleRejectConfirm}
								disabled={actionLoading || !rejectRemarks.trim()}
							>
								{actionLoading ? 'Rejecting...' : 'Reject Task'}
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

TaskDetailsTab.propTypes = {
	task: PropTypes.object,
	taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	workflow: PropTypes.object,
	onTaskUpdate: PropTypes.func,
	onRefresh: PropTypes.func,
	onTaskAction: PropTypes.func,
	actionLoading: PropTypes.bool,
}

export default TaskDetailsTab
